import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { cardHref } from '@/lists/cellValue'
import type { ListRow } from '@/lists/types'
import { HistoryPanel } from './HistoryPanel'
import {
  allocatePayment,
  autoMatchPayment,
  confirmPayment,
  fetchPayment,
  fetchPaymentAllocations,
  reversePayment,
  type Payment,
  type PaymentAllocationLine,
} from './invoiceApi'
import { ConfirmModal, Facts, Panel, RecordHeader, RecordTabs, StageBar, StateView, type Fact, type HeaderAction, type MenuAction, type StageStep } from './record'
import { formatMoment, listPath } from './recordHelpers'
import { FailureAlert, RecordFailure, RecordSkeleton, StatusBadge, copyLinkItem, failureText, labelOf, numberOf } from './recordKit'
import type { RecordViewProps } from './types'

/**
 * Карточка платежа (Payment.dc.html, тикет #78).
 *
 * Главный вопрос карточки — неразнесённый остаток: он выделен в фактах, а вкладка
 * «Разнесение по счетам» показывает неоплаченные счета плательщика с полем суммы у каждого.
 * Сумма по умолчанию — меньшее из остатка долга и остатка платежа; пара (платёж, счёт)
 * уникальна на сервере, и повторное разнесение на тот же счёт **увеличивает** строку.
 *
 * Неподтверждённый платёж (холд) разносить нельзя — основным действием у него стоит
 * «Подтвердить». Сторнирование — в «⋯» за чертой, с подтверждением; право `delete`, а не
 * `update`: отменяется зафиксированный денежный факт.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * Уже сделанные разнесения — над списком счетов, с сервера (`/payments/{id}/allocations`):
 * счёт, его номер, сумма, когда и как (автосверка или вручную). Сумма разнесённого в
 * сводке по-прежнему считается разницей суммы и остатка — это одна цифра из двух полей
 * платежа, и считать её второй раз по строкам значило бы завести ей второй источник.
 * - **Банк, БИК и счёт плательщика, очерёдность, строка выписки** — платёж хранит только
 *   имя, ИНН и счёт плательщика (`payerAccount`).
 * - **«Вернуть плательщику», «Учесть как аванс», «Открыть выписку»** — операций нет;
 *   неразнесённый остаток и есть аванс контрагента.
 * - **Долг и просрочка контрагента «до → после»** — сводки дебиторки по одной валюте нет
 *   (см. карточку сделки).
 * - **Лента платежа** — у платежа нет ленты взаимодействий; изменения — во вкладке «История».
 */
export function PaymentRecordView({ recordId, record, context, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('alloc')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [confirmReverse, setConfirmReverse] = useState(false)

  const payment = useQuery({ queryKey: ['payment', recordId], queryFn: ({ signal }) => fetchPayment(recordId, signal), retry: false })
  const made = useQuery({
    queryKey: ['payment-allocations', recordId],
    queryFn: ({ signal }) => fetchPaymentAllocations(recordId, signal),
    retry: false,
  })
  const counterpartyId = payment.data?.counterpartyId ?? null
  const mayReadInvoices = can(user, 'billing.invoice', 'read')
  const invoices = useQuery({
    queryKey: ['payment-invoices', counterpartyId, payment.data?.currency],
    enabled: Boolean(counterpartyId) && mayReadInvoices,
    queryFn: ({ signal }) =>
      fetchPage(
        'billing.invoice',
        {
          columns: ['number', 'issued_on', 'due_on', 'total', 'paid_total', 'currency', 'status'],
          filters: [
            { column: 'counterparty_id', operator: 'eq', value: counterpartyId },
            // Only what can still take money: the same statuses the server allocates to.
            { column: 'status', operator: 'in', value: ['sent', 'partially_paid', 'overdue'] },
          ],
          sort: [{ column: 'issued_on', descending: false }],
          pageSize: 100,
        },
        null,
        signal,
      ),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['payment', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['payment-allocations', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['payment-invoices'] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'payments.payment', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'payments.payment'] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'billing.invoice'] })
  }
  const fail = (error: unknown) => {
    setNotice(null)
    setFailure(failureText(error, t('app.error')))
  }
  const succeed = (text: string | null) => {
    setFailure(null)
    setNotice(text)
    refresh()
  }

  const confirm = useMutation({ mutationFn: () => confirmPayment(recordId), onSuccess: () => succeed(t('record.pay.confirmed')), onError: fail })
  const autoMatch = useMutation({
    mutationFn: () => autoMatchPayment(recordId),
    onSuccess: (result) => succeed(result.matched ? t('record.pay.matched', { count: result.allocations.length }) : t('record.pay.notMatched')),
    onError: fail,
  })
  const allocate = useMutation({
    mutationFn: ({ invoiceId, amount }: { invoiceId: string; amount: number }) => allocatePayment(recordId, invoiceId, amount),
    onSuccess: () => {
      setAmounts({})
      succeed(t('record.inv.allocated'))
    },
    onError: fail,
  })
  const reverse = useMutation({
    mutationFn: () => reversePayment(recordId),
    onSuccess: () => {
      setConfirmReverse(false)
      succeed(t('record.pay.reversed'))
    },
    onError: (error) => {
      setConfirmReverse(false)
      fail(error)
    },
  })

  if (payment.isLoading) return <RecordSkeleton />
  if (!payment.data) {
    return <RecordFailure error={payment.error} onRetry={() => void payment.refetch()} listPath={listPath('payments.payment', '/payments')} />
  }
  const current = payment.data
  const mayUpdate = can(user, 'payments.payment', 'update')
  const mayReverse = can(user, 'payments.payment', 'delete')
  const money = (value: number) => formatMoney(value, current.currency, locale)
  const allocated = Math.round((current.amount - current.unallocatedAmount) * 100) / 100
  const busy = confirm.isPending || autoMatch.isPending || allocate.isPending || reverse.isPending
  const reversed = current.status === 'reversed'
  const canAllocate = mayUpdate && current.allocatable && current.unallocatedAmount > 0

  // ------------------------------------------------------------------ шапка

  let primary: HeaderAction | null = null
  if (mayUpdate && current.status === 'pending') {
    primary = { id: 'confirm', label: t('record.pay.confirm'), icon: 'check', onClick: () => confirm.mutate(), disabled: busy }
  } else if (canAllocate && current.counterpartyId) {
    primary = { id: 'auto', label: t('record.pay.autoMatch'), icon: 'refresh', onClick: () => autoMatch.mutate(), disabled: busy }
  }
  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]
  if (mayReverse && !reversed && current.status !== 'pending') {
    menu.push({ id: 'reverse', label: t('record.pay.reverseContinue'), icon: 'close', danger: true, onSelect: () => setConfirmReverse(true) })
  }

  const counterpartyName = labelOf(record.values, 'counterparty_id') ?? current.payerName
  const counterpartyHref = current.counterpartyId ? cardHref(user, 'sales.counterparty', current.counterpartyId) : null
  const meta = [
    <span key="direction" className="badge b-info">
      {t(`value.payments.payment.direction.${current.direction}`)}
    </span>,
    <StatusBadge key="status" entity="payments.payment" value={current.status} />,
    counterpartyName ? (
      <span key="payer" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {counterpartyHref ? <Link to={counterpartyHref}>{counterpartyName}</Link> : counterpartyName}
      </span>
    ) : (
      <span key="payer" className="flag flag-warning">
        <Icon name="alert" />
        {t('record.pay.noPayer')}
      </span>
    ),
    current.payerInn ? <span key="inn" className="mono t-sm muted">{t('record.lead.inn', { inn: current.payerInn })}</span> : null,
    current.purpose ? <span key="purpose">{t('record.pay.purposeQuoted', { purpose: current.purpose })}</span> : null,
    t(`value.payments.payment.method.${current.method}`),
  ]

  // ------------------------------------------------------------------ этапы и факты

  const order = ['pending', 'confirmed', 'partial', 'matched']
  const reached = reversed
    ? -1
    : current.status === 'matched' || (current.status === 'confirmed' && current.unallocatedAmount <= 0)
      ? 3
      : current.status === 'confirmed' && allocated > 0
        ? 2
        : current.status === 'confirmed'
          ? 1
          : 0
  const steps: StageStep[] = order.map((code, index) => ({
    id: code,
    label: t(`record.pay.stage.${code}`),
    state: index < reached ? 'done' : index === reached ? 'current' : 'future',
    meta: code === 'partial' && allocated > 0 && current.unallocatedAmount > 0 ? money(allocated) : undefined,
  }))

  const share = current.amount > 0 ? Math.round((current.unallocatedAmount / current.amount) * 100) : 0
  const facts: Fact[] = [
    { id: 'amount', label: t('record.pay.fact.amount'), value: money(current.amount), sub: t(`value.payments.payment.direction.${current.direction}`) },
    {
      id: 'allocated',
      label: t('record.pay.fact.allocated'),
      value: <span className={allocated > 0 ? 'is-success' : undefined}>{money(allocated)}</span>,
    },
    {
      id: 'unallocated',
      label: t('record.pay.fact.unallocated'),
      value: money(current.unallocatedAmount),
      sub:
        current.unallocatedAmount > 0 ? (
          <span className="flag flag-warning">
            <Icon name="alert" />
            {t('record.pay.unallocatedShare', { share: formatNumber(share, locale) })}
          </span>
        ) : (
          t('record.pay.fullyAllocated')
        ),
    },
    {
      id: 'account',
      label: t('record.pay.fact.account'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{labelOf(record.values, 'organization_id') ?? t('record.notSet')}</span>,
      sub: labelOf(record.values, 'bank_account_id') ?? undefined,
    },
    {
      id: 'date',
      label: t('record.pay.fact.date'),
      value: formatDate(current.paymentDate, locale),
      sub: current.valueDate ? t('record.pay.valueDate', { date: formatDate(current.valueDate, locale) }) : undefined,
    },
  ]

  // ------------------------------------------------------------------ разнесение

  const rows = (invoices.data?.rows ?? []).filter((row) => String(row.currency ?? current.currency) === current.currency)
  const outstanding = (row: ListRow) => Math.max(0, Math.round(((numberOf(row, 'total') ?? 0) - (numberOf(row, 'paid_total') ?? 0)) * 100) / 100)
  const suggested = (row: ListRow) => Math.min(outstanding(row), current.unallocatedAmount)
  const amountOf = (row: ListRow): number | null => {
    const raw = amounts[row.id]
    if (raw === undefined) return suggested(row)
    const parsed = Number(raw.replace(/\s/g, '').replace(',', '.'))
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null
  }

  const allocation = !current.counterpartyId ? (
    <StateView boxed kind="setup" title={t('record.pay.alloc.noPayer')} description={t('record.pay.alloc.noPayerText')} />
  ) : !mayReadInvoices ? (
    <StateView compact kind="forbidden" title={t('record.pay.alloc.forbidden')} />
  ) : invoices.isLoading ? (
    <p className="muted">{t('app.loading')}</p>
  ) : invoices.isError ? (
    <StateView
      compact
      kind={invoices.error instanceof ApiError && invoices.error.isForbidden ? 'forbidden' : 'error'}
      title={t('record.pay.alloc.error')}
    />
  ) : rows.length === 0 ? (
    <StateView boxed kind="empty" title={t('record.pay.alloc.empty', { payer: counterpartyName ?? '' })} description={t('record.pay.alloc.emptyText')} />
  ) : (
    <>
      <div className="toolbar">
        <span className="h2">{t('record.pay.alloc.title')}</span>
        <span className="t-sm muted">{t('record.pay.alloc.count', { count: rows.length })}</span>
      </div>
      {!current.allocatable && !reversed && (
        <div className="alert alert-warning" role="status">
          <Icon name="alert" />
          <span>{t('record.pay.alloc.notAllocatable')}</span>
        </div>
      )}
      <div className="tbl-wrap">
        <table className="tbl" aria-label={t('record.pay.alloc.title')}>
          <thead>
            <tr>
              <th>{t('record.pay.col.invoice')}</th>
              <th>{t('column.issued_on')}</th>
              <th>{t('column.due_on')}</th>
              <th className="num">{t('record.pay.col.total')}</th>
              <th className="num">{t('record.pay.col.outstanding')}</th>
              {canAllocate && <th className="num">{t('record.pay.col.allocate')}</th>}
              {canAllocate && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const amount = amountOf(row)
              const dueOn = typeof row.due_on === 'string' ? row.due_on : null
              return (
                <tr key={row.id}>
                  <td>
                    <span className="stack" style={{ gap: 0 }}>
                      <Link to={`${listPath('billing.invoice', '/invoices')}/${row.id}`} className="mono">
                        {String(row.number ?? row.id)}
                      </Link>
                      <StatusBadge entity="billing.invoice" value={typeof row.status === 'string' ? row.status : null} />
                    </span>
                  </td>
                  <td>{typeof row.issued_on === 'string' ? formatDate(row.issued_on, locale) : '—'}</td>
                  <td>{dueOn ? <DueOn date={dueOn} /> : '—'}</td>
                  <td className="num">{money(numberOf(row, 'total') ?? 0)}</td>
                  <td className="num">{money(outstanding(row))}</td>
                  {canAllocate && (
                    <td className="num">
                      <input
                        className="input input-sm alloc-input"
                        inputMode="decimal"
                        aria-label={t('record.pay.amountFor', { number: String(row.number ?? '') })}
                        value={amounts[row.id] ?? String(suggested(row))}
                        onChange={(event) => setAmounts((previous) => ({ ...previous, [row.id]: event.target.value }))}
                      />
                    </td>
                  )}
                  {canAllocate && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={busy || amount == null || amount > current.unallocatedAmount}
                        onClick={() => amount != null && allocate.mutate({ invoiceId: row.id, amount })}
                      >
                        {t('record.pay.allocate')}
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="panel" style={{ padding: '10px 14px' }}>
        <span className="t-md">
          {t('record.pay.alloc.summary', { allocated: money(allocated), amount: money(current.amount), left: money(current.unallocatedAmount) })}
        </span>
      </div>
      {current.unallocatedAmount > 0 && (
        <div className="alert alert-info" role="status">
          <Icon name="payments" />
          <span className="fg">{t('record.pay.alloc.advance', { left: money(current.unallocatedAmount) })}</span>
        </div>
      )}
    </>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.paymentRegistry'), to: listPath('payments.payment', '/payments') }, { label: current.number ?? current.id.slice(0, 8), mono: true }]}
        title={
          <>
            {t('record.pay.title')} <span className="rec-title-mono">{current.number ?? '—'}</span>{' '}
            <span className="rec-title-date">{t('record.doc.from', { date: formatDate(current.paymentDate, locale) })}</span>
          </>
        }
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      <FailureAlert text={failure} />
      {notice && (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          <span>{notice}</span>
        </div>
      )}
      {notices}
      {reversed && (
        <div className="alert alert-neutral" role="status">
          <Icon name="close" />
          <span>{t('record.pay.reversedText')}</span>
        </div>
      )}
      <StageBar steps={steps} ariaLabel={t('record.pay.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'alloc', label: t('record.pay.tab.alloc'), count: invoices.isSuccess ? rows.length : null },
          { id: 'details', label: t('record.pay.tab.details') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.pay.sections')}
      >
        {tab === 'alloc' && (
          <div className="stack" style={{ gap: 12, paddingTop: 14, minWidth: 0 }}>
            <MadeAllocations query={made} currency={current.currency} />
            {allocation}
          </div>
        )}
        {tab === 'details' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.pay.details')}>
              <dl className="kv" style={{ gridTemplateColumns: '180px minmax(0, 1fr)', marginBottom: 12 }}>
                <dt>{t('column.payer_name')}</dt>
                <dd>{current.payerName ?? '—'}</dd>
                <dt>{t('column.payer_inn')}</dt>
                <dd className="mono">{current.payerInn ?? '—'}</dd>
                <dt>{t('record.pay.payerAccount')}</dt>
                <dd className="mono">{current.payerAccount ?? '—'}</dd>
                <dt>{t('column.purpose')}</dt>
                <dd>{current.purpose ?? '—'}</dd>
                <dt>{t('column.external_id')}</dt>
                <dd className="mono">{current.externalId ?? '—'}</dd>
              </dl>
              {form}
            </Panel>
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirmReverse}
        icon="close"
        title={t('record.pay.reverse.title', { number: current.number ?? '' })}
        confirmLabel={t('record.pay.reverse')}
        pending={reverse.isPending}
        onCancel={() => setConfirmReverse(false)}
        onConfirm={() => reverse.mutate()}
      >
        <p>{t('record.pay.reverse.text', { allocated: money(allocated) })}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/** Срок оплаты словом: «просрочен N дн.» — иконкой и словом, не цветом. */
function DueOn({ date }: { date: string }) {
  const { t, locale } = useApp()
  const due = new Date(`${date}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)
  return (
    <span className="row" style={{ gap: 8 }}>
      <span>{formatDate(date, locale)}</span>
      {days < 0 && (
        <span className="flag flag-danger">
          <Icon name="alert" />
          {t('record.inv.overdueDays', { days: -days })}
        </span>
      )}
    </span>
  )
}

export type { Payment }

/**
 * Сделанные разнесения платежа.
 *
 * Отказ и сбой называются словами, а не пустотой: «разнесений нет» и «не удалось узнать»
 * на экране выглядели бы одинаково, а значат противоположное.
 */
function MadeAllocations({ query, currency }: {
  query: { isLoading: boolean; error: unknown; data: PaymentAllocationLine[] | undefined }
  currency: string
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (query.error) {
    return (
      <StateView
        compact
        kind={query.error instanceof ApiError && query.error.isForbidden ? 'forbidden' : 'error'}
        title={t('record.pay.made.error')}
      />
    )
  }
  const lines = query.data ?? []
  return (
    <Panel title={t('record.pay.made.title')} count={lines.length} flush>
      {lines.length === 0 ? (
        <div className="panel-b"><span className="muted">{t('record.pay.made.empty')}</span></div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('record.pay.made.title')}>
            <thead>
              <tr>
                <th>{t('record.pay.col.invoice')}</th>
                <th>{t('record.pay.made.at')}</th>
                <th>{t('record.pay.made.rule')}</th>
                <th className="num">{t('record.pay.made.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td>
                    <Link to={`${listPath('billing.invoice', '/invoices')}/${line.invoiceId}`} className="mono">
                      {line.invoiceNumber ?? line.invoiceId.slice(0, 8)}
                    </Link>
                  </td>
                  <td>{formatMoment(line.allocatedAt, locale)}</td>
                  <td>{t(`record.pay.made.rule.${line.rule}`)}</td>
                  <td className="num">{formatMoney(line.amount, line.currency ?? currency, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
